import React, { useState, useEffect } from 'react';
import { 
  Package, CheckCircle, XCircle, Clock, Star, Eye, Edit3,
  Search, Filter, Plus, Save, X, AlertTriangle, Image,
  Tag, Users, Calendar, ExternalLink, Trash2, FileText, ChevronsLeft, ChevronRight, ChevronLeft,ChevronsRight, Award 
} from 'lucide-react';
import API_URL from '../config/ApiUrl';
import { useTheme } from '../arrangement/ThemeContext';
import { FeaturedProductsConfig } from './FeaturedProductsHomeComponent';
export const ProductManagement = () => {
  const [uploadedProducts, setUploadedProducts] = useState([]);
  const [existingProducts, setExistingProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showAttributeModal, setShowAttributeModal] = useState(false);
  const [productAttributes, setProductAttributes] = useState({});
  const [selectedProductType, setSelectedProductType] = useState('flower');
  
  const { colors } = useTheme(); 

  const [showFeaturedConfig, setShowFeaturedConfig] = useState(false);

  const [editProductImages, setEditProductImages] = useState([]);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [coverImageUrl, setCoverImageUrl] = useState('');

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  const [selectedCoverFile, setSelectedCoverFile] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  // Add sorting states
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  // Add debounced search
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);

  // Edit Modal States
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editProductData, setEditProductData] = useState({
    name: '',
    brand: '',
    type: 'flower',
    attributes: {}
  });

  const [showAdminUploadModal, setShowAdminUploadModal] = useState(false);
  const [adminUploadData, setAdminUploadData] = useState({
    name: '',
    brand: '',
    type: 'flower',
    attributes: {
        THC: '',
        CBD: '',
        terpenes: [],
        strain_type: 'Hybrid',
        size: '3.5g'
    }
  });
  useEffect(() => {
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }
    
    const timer = setTimeout(() => {
      setCurrentPage(1); // Reset to first page on search
      loadProductData();
    }, 500);
    
    setSearchDebounceTimer(timer);
    
    return () => clearTimeout(timer);
  }, [searchTerm, filterType, sortBy, sortOrder]);

  // Load data on component mount
  useEffect(() => {
    loadProductData();
  }, []);

  const loadProductData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        per_page: perPage.toString(),
        search: searchTerm,
        type: filterType,
        sort_by: sortBy,
        sort_order: sortOrder
      });

      const [uploadedRes, existingRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/uploaded-products`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/products?${params}`, { credentials: 'include' })
      ]);

      if (uploadedRes.ok && existingRes.ok) {
        const uploadedData = await uploadedRes.json();
        const existingData = await existingRes.json();
        
        setUploadedProducts(uploadedData.products || []);
        setExistingProducts(existingData.products || []);
        
        // Update pagination info
        if (existingData.pagination) {
          setTotalPages(existingData.pagination.pages);
          setTotalProducts(existingData.pagination.total);
          setHasNext(existingData.pagination.has_next);
          setHasPrev(existingData.pagination.has_prev);
        }
      }
    } catch (error) {
      console.error('Failed to load product data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Edit Product Functions
  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setEditProductData({
      name: product.name,
      brand: product.brand,
      type: product.type,
      attributes: { ...product.attributes }
    });

      setEditProductImages(product.images || []);
      setCoverImageUrl(product.coverImage?.imageUrl || '');

      setTerpenesInputValue(product.attributes.terpenes ? product.attributes.terpenes.join(', ') : '');

    setShowEditModal(true);
  };

  const handleAddImage = () => {
    if (!newImageUrl.trim()) return;
    
    const newImage = {
      id: Date.now(), // Temporary ID for new images
      imageUrl: newImageUrl.trim(),
      isCover: editProductImages.length === 0 // First image becomes cover
    };
    
    setEditProductImages(prev => [...prev, newImage]);
    
    // Set as cover if it's the first image
    if (editProductImages.length === 0) {
      setCoverImageUrl(newImageUrl.trim());
    }
    
    setNewImageUrl('');
  };

  const handleRemoveImage = (imageToRemove) => {
    setEditProductImages(prev => prev.filter(img => img.id !== imageToRemove.id));
    
    // If we removed the cover image, set another as cover
    if (coverImageUrl === imageToRemove.imageUrl) {
      const remainingImages = editProductImages.filter(img => img.id !== imageToRemove.id);
      setCoverImageUrl(remainingImages.length > 0 ? remainingImages[0].imageUrl : '');
    }
  };

  const handleSetCoverImage = (imageUrl) => {
    setCoverImageUrl(imageUrl);
  };

  const handleSaveEditedProduct = async () => {
    if (!editingProduct) return;

    setActionLoading(true);
    try {
      // NOW we save all the S3 URLs to the database
      const updateData = {
        ...editProductData,
        images: editProductImages.map(img => img.imageUrl), // These are S3 URLs
        coverImageUrl: coverImageUrl
      };

      const response = await fetch(`${API_URL}/api/admin/products/${editingProduct.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        const updatedProduct = await response.json();
        
        // Update the existing products list
        setExistingProducts(prev => 
          prev.map(p => p.id === editingProduct.id 
            ? updatedProduct.product
            : p
          )
        );
        setShowEditModal(false);
        setEditingProduct(null);
        setEditProductImages([]);
        setCoverImageUrl('');
        setSelectedFiles([]); // Clear selected files
      }
    } catch (error) {
      console.error('Failed to update product:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    // if (!confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
    //   return;
    // }

    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/products/${productId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setExistingProducts(prev => prev.filter(p => p.id !== productId));
      }
    } catch (error) {
      console.error('Failed to delete product:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditAttributeChange = (key, value) => {
    setEditProductData(prev => ({
      ...prev,
      attributes: {
        ...prev.attributes,
        [key]: value
      }
    }));
  };

  const handleEditTypeChange = (newType) => {
    let defaultAttributes = {};
    
    if (newType === 'cart') {
      defaultAttributes = {
        THC: editProductData.attributes.THC || '',
        CBD: editProductData.attributes.CBD || '',
        flavor: editProductData.attributes.flavor || '',
        strain_type: editProductData.attributes.strain_type || 'Hybrid',
        category: editProductData.attributes.category || 'Distillate',
        size: editProductData.attributes.size || '1g'
      };
    } else if (newType === 'edible') {
      defaultAttributes = {
        THC: editProductData.attributes.THC || '',
        CBD: editProductData.attributes.CBD || '',
        flavor: editProductData.attributes.flavor || '',
        strain_type: editProductData.attributes.strain_type || 'Hybrid',
        category: editProductData.attributes.category || 'Gummies',
        dosage_per_unit: editProductData.attributes.dosage_per_unit || '',
        size: editProductData.attributes.size || ''
      };
    } else if (newType === 'pre-roll') {
      defaultAttributes = {
        THC: editProductData.attributes.THC || '',
        CBD: editProductData.attributes.CBD || '',
        strain_type: editProductData.attributes.strain_type || 'Hybrid',
        terpenes: editProductData.attributes.terpenes || [],
        size: editProductData.attributes.size || '1g',
        quantity: editProductData.attributes.quantity || '1-Pack'
      };
    } else if (newType === 'concentrate') {
      defaultAttributes = {
        THC: editProductData.attributes.THC || '',
        CBD: editProductData.attributes.CBD || '',
        strain_type: editProductData.attributes.strain_type || 'Hybrid',
        category: editProductData.attributes.category || 'Shatter',
        size: editProductData.attributes.size || '1g'
      };
    } else if (newType === 'other') {
      defaultAttributes = {
        THC: editProductData.attributes.THC || '',
        CBD: editProductData.attributes.CBD || '',
        strain_type: editProductData.attributes.strain_type || 'CBD',
        category: editProductData.attributes.category || 'Tincture',
        size: editProductData.attributes.size || '',
        flavor: editProductData.attributes.flavor || ''
      };
    } else {
      // flower
      defaultAttributes = {
        THC: editProductData.attributes.THC || '',
        CBD: editProductData.attributes.CBD || '',
        terpenes: editProductData.attributes.terpenes || [],
        strain_type: editProductData.attributes.strain_type || 'Hybrid',
        size: editProductData.attributes.size || '3.5g'
      };
    }
    
    setEditProductData(prev => ({
      ...prev,
      type: newType,
      attributes: defaultAttributes
    }));
  };

  // Original functions (keeping them as they were)
  const handleApproveProduct = (product) => {
    setSelectedProduct(product);
    setShowAttributeModal(true);
    
    // Pass existing attributes to initialization
    initializeAttributes(product.product_name, product.attributes);
  };

  const initializeAttributes = (productName, existingAttributes = null) => {
    const name = productName.toLowerCase();
    let type = 'flower';
    let defaultAttributes = {};

    // First, determine product type from name (as fallback)
    if (name.includes('cart') || name.includes('vape')) {
      type = 'cart';
    } else if (name.includes('gummy') || name.includes('edible') || name.includes('chocolate') || name.includes('cookie')) {
      type = 'edible';
    } else if (name.includes('pre-roll') || name.includes('joint')) {
      type = 'pre-roll';
    } else if (name.includes('shatter') || name.includes('wax') || name.includes('rosin') || name.includes('live resin')) {
      type = 'concentrate';
    } else if (name.includes('tincture') || name.includes('capsule') || name.includes('topical')) {
      type = 'other';
    }

    // If we have existing attributes, try to determine type from them
    if (existingAttributes) {
      // Check if existing attributes suggest a different type
      if (existingAttributes.flavor && existingAttributes.category && 
          ['Distillate', 'Live Resin', 'Rosin', 'CO2'].includes(existingAttributes.category)) {
        type = 'cart';
      } else if (existingAttributes.dosage_per_unit || existingAttributes.flavor) {
        type = 'edible';
      } else if (existingAttributes.quantity && existingAttributes.quantity.includes('Pack')) {
        type = 'pre-roll';
      } else if (existingAttributes.category && 
                ['Shatter', 'Wax', 'Rosin', 'Live Resin'].includes(existingAttributes.category)) {
        type = 'concentrate';
      }
    }

    // Set up default attributes based on type, merging with existing data
    if (type === 'cart') {
      defaultAttributes = {
        THC: existingAttributes?.THC || '',
        CBD: existingAttributes?.CBD || '',
        flavor: existingAttributes?.flavor || '',
        strain_type: existingAttributes?.strain_type || 'Hybrid',
        category: existingAttributes?.category || 'Distillate',
        size: existingAttributes?.size || '1g',
        terpenes: existingAttributes?.terpenes || [],
      };
    } else if (type === 'edible') {
      defaultAttributes = {
        THC: existingAttributes?.THC || '',
        CBD: existingAttributes?.CBD || '',
        flavor: existingAttributes?.flavor || '',
        strain_type: existingAttributes?.strain_type || 'Hybrid',
        category: existingAttributes?.category || 'Gummies',
        dosage_per_unit: existingAttributes?.dosage_per_unit || '',
        size: existingAttributes?.size || ''
      };
    } else if (type === 'pre-roll') {
      defaultAttributes = {
        THC: existingAttributes?.THC || '',
        CBD: existingAttributes?.CBD || '',
        strain_type: existingAttributes?.strain_type || 'Hybrid',
        terpenes: existingAttributes?.terpenes || [],
        size: existingAttributes?.size || '1g',
        quantity: existingAttributes?.quantity || '1-Pack'
      };
    } else if (type === 'concentrate') {
      defaultAttributes = {
        THC: existingAttributes?.THC || '',
        CBD: existingAttributes?.CBD || '',
        strain_type: existingAttributes?.strain_type || 'Hybrid',
        category: existingAttributes?.category || 'Shatter',
        size: existingAttributes?.size || '1g'
      };
    } else if (type === 'other') {
      defaultAttributes = {
        THC: existingAttributes?.THC || '',
        CBD: existingAttributes?.CBD || '',
        strain_type: existingAttributes?.strain_type || 'CBD',
        category: existingAttributes?.category || 'Tincture',
        size: existingAttributes?.size || '',
        flavor: existingAttributes?.flavor || ''
      };
    } else {
      // flower
      defaultAttributes = {
        THC: existingAttributes?.THC || '',
        CBD: existingAttributes?.CBD || '',
        terpenes: existingAttributes?.terpenes || [],
        strain_type: existingAttributes?.strain_type || 'Hybrid',
        size: existingAttributes?.size || '3.5g'
      };
    }

    setSelectedProductType(type);
    setProductAttributes(defaultAttributes);
  };

  const handleSaveProduct = async () => {
    if (!selectedProduct) return;

    setActionLoading(true);
    try {
      const productData = {
        name: selectedProduct.product_name,
        brand: selectedProduct.brand,
        type: selectedProductType,
        attributes: productAttributes,
        uploadedProductId: selectedProduct.id
      };

      const response = await fetch(`${API_URL}/api/admin/approve-product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(productData)
      });

      if (response.ok) {
        setUploadedProducts(prev => prev.filter(p => p.id !== selectedProduct.id));
        await loadProductData();
        setShowAttributeModal(false);
        setSelectedProduct(null);
      }
    } catch (error) {
      console.error('Failed to approve product:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDenyProduct = async (productId) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/deny-product/${productId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setUploadedProducts(prev => prev.filter(p => p.id !== productId));
      }
    } catch (error) {
      console.error('Failed to deny product:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAdminUpload = async () => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/direct-upload-product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(adminUploadData)
      });

      if (response.ok) {
        await loadProductData();
        setShowAdminUploadModal(false);
        setAdminUploadData({
          name: '',
          brand: '',
          type: 'flower',
          attributes: {
            THC: '',
            CBD: '',
            terpenes: [],
            strain_type: 'Hybrid',
            size: '3.5g'
          }
        });
      }
    } catch (error) {
      console.error('Failed to upload product:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAdminAttributeChange = (key, value) => {
    setAdminUploadData(prev => ({
      ...prev,
      attributes: {
        ...prev.attributes,
        [key]: value
      }
    }));
  };

  const handleAdminTypeChange = (newType) => {
    let defaultAttributes = {};
    
    if (newType === 'cart') {
      defaultAttributes = {
        THC: '',
        CBD: '',
        flavor: '',
        strain_type: 'Hybrid',
        category: 'Distillate',
        size: '1g'
      };
    } else if (newType === 'edible') {
      defaultAttributes = {
        THC: '',
        CBD: '',
        flavor: '',
        strain_type: 'Hybrid',
        category: 'Gummies',
        dosage_per_unit: '',
        size: ''
      };
    } else if (newType === 'pre-roll') {
      defaultAttributes = {
        THC: '',
        CBD: '',
        strain_type: 'Hybrid',
        terpenes: [],
        size: '1g',
        quantity: '1-Pack'
      };
    } else if (newType === 'concentrate') {
      defaultAttributes = {
        THC: '',
        CBD: '',
        strain_type: 'Hybrid',
        category: 'Shatter',
        size: '1g'
      };
    } else if (newType === 'other') {
      defaultAttributes = {
        THC: '',
        CBD: '',
        strain_type: 'CBD',
        category: 'Tincture',
        size: '',
        flavor: ''
      };
    } else {
      defaultAttributes = {
        THC: '',
        CBD: '',
        terpenes: [],
        strain_type: 'Hybrid',
        size: '3.5g'
      };
    }
    
    setAdminUploadData(prev => ({
      ...prev,
      type: newType,
      attributes: defaultAttributes
    }));
  };

  const handleFileSelect = (e) => {
  const files = Array.from(e.target.files);
  setSelectedFiles(files);
};
const handleCoverFileSelect = (e) => {
  const file = e.target.files[0];
  setSelectedCoverFile(file);
};

const handleSearchChange = (value) => {
  setSearchTerm(value);
  // The useEffect will handle the debounced API call
};

// Quick page jump function
const handleQuickPageJump = () => {
  const page = prompt(`Jump to page (1-${totalPages}):`);
  const pageNum = parseInt(page);
  if (pageNum && pageNum >= 1 && pageNum <= totalPages) {
    setCurrentPage(pageNum);
  }
};


const handleUploadImages = async () => {
  if (selectedFiles.length === 0) return;

  setUploadingImages(true);
  try {
    const formData = new FormData();
    
    // Add each selected file
    selectedFiles.forEach((file) => {
      formData.append('images', file);
    });

    // Upload to S3 immediately - NOT saving to DB yet
    const response = await fetch(`${API_URL}/api/admin/products/${editingProduct.id}/upload-images`, {
      method: 'POST',
      credentials: 'include',
      body: formData
    });

    if (response.ok) {
      const result = await response.json();
      
      // Add the S3 URLs to frontend state (NOT DB yet)
      const newImages = result.image_urls.map((url, index) => ({
        id: Date.now() + index,
        imageUrl: url,
        isCover: false
      }));
      
      setEditProductImages(prev => [...prev, ...newImages]);
      
      // Clear the file input
      setSelectedFiles([]);
      document.getElementById('image-file-input').value = '';
      
      console.log('Images uploaded to S3:', result.image_urls);
    }
  } catch (error) {
    console.error('Failed to upload images:', error);
  } finally {
    setUploadingImages(false);
  }
};


const handleUploadCoverImage = async () => {
  if (!selectedCoverFile) return;

  setUploadingCover(true);
  try {
    const formData = new FormData();
    formData.append('cover_image', selectedCoverFile);

    // Upload cover image to S3 immediately - NOT saving to DB yet
    const response = await fetch(`${API_URL}/api/admin/products/${editingProduct.id}/upload-cover-image`, {
      method: 'POST',
      credentials: 'include',
      body: formData
    });

    if (response.ok) {
      const result = await response.json();
      
      // Add cover image to state and set as cover
      const newCoverImage = {
        id: Date.now(),
        imageUrl: result.cover_image_url,
        isCover: true
      };
      
      // Add to images list if not already there
      const existingImageIndex = editProductImages.findIndex(img => img.imageUrl === result.cover_image_url);
      if (existingImageIndex === -1) {
        setEditProductImages(prev => [...prev, newCoverImage]);
      }
      
      // Set as cover image
      setCoverImageUrl(result.cover_image_url);
      
      // Clear the file input
      setSelectedCoverFile(null);
      document.getElementById('cover-file-input').value = '';
      
      console.log('Cover image uploaded to S3:', result.cover_image_url);
    }
  } catch (error) {
    console.error('Failed to upload cover image:', error);
  } finally {
    setUploadingCover(false);
  }
};

const [terpenesInputValue, setTerpenesInputValue] = useState('');



  // Shared attribute form renderer (works for both approval and edit)
  const renderAttributeForm = (isEdit = false) => {
    const currentAttributes = isEdit ? editProductData.attributes : productAttributes;
    const currentType = isEdit ? editProductData.type : selectedProductType;
    const handleChange = isEdit ? handleEditAttributeChange : 
      ((key, value) => setProductAttributes(prev => ({ ...prev, [key]: value })));

    const handleTerpenesChange = (value) => {
      setTerpenesInputValue(value); // Store raw input
      const terpenes = value.split(',').map(t => t.trim()).filter(t => t);
      handleChange('terpenes', terpenes);
    };

    const commonFields = (
    <>
      <div style={{ marginBottom: '16px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          color: '#e5e5e5',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          THC Content
        </label>
        <input
          type="text"
          placeholder="e.g., 21%"
          value={currentAttributes.THC || ''}
          onChange={(e) => handleChange('THC', e.target.value)}
          style={{
            width: '100%',
            padding: '12px',
            background: '#2a2a2a',
            border: '2px solid #333',
            borderRadius: '8px',
            color: '#e5e5e5',
            fontSize: '14px',
            outline: 'none',
            boxSizing: 'border-box'
          }}
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          color: '#e5e5e5',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          CBD Content
        </label>
        <input
          type="text"
          placeholder="e.g., 0.5%"
          value={currentAttributes.CBD || ''}
          onChange={(e) => handleChange('CBD', e.target.value)}
          style={{
            width: '100%',
            padding: '12px',
            background: '#2a2a2a',
            border: '2px solid #333',
            borderRadius: '8px',
            color: '#e5e5e5',
            fontSize: '14px',
            outline: 'none',
            boxSizing: 'border-box'
          }}
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          color: '#e5e5e5',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          Terpenes (comma-separated)
        </label>
        <input
        type="text"
        placeholder="e.g., myrcene, limonene, caryophyllene"
        value={terpenesInputValue}  // USE THIS INSTEAD
        onChange={(e) => handleTerpenesChange(e.target.value)}
          style={{
            width: '100%',
            padding: '12px',
            background: '#2a2a2a',
            border: '2px solid #333',
            borderRadius: '8px',
            color: '#e5e5e5',
            fontSize: '14px',
            outline: 'none',
            boxSizing: 'border-box'
          }}
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          color: '#e5e5e5',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          Strain Type
        </label>
        <select
          value={currentAttributes.strain_type || 'Hybrid'}
          onChange={(e) => handleChange('strain_type', e.target.value)}
          style={{
            width: '100%',
            padding: '12px',
            background: '#2a2a2a',
            border: '2px solid #333',
            borderRadius: '8px',
            color: '#e5e5e5',
            fontSize: '14px',
            outline: 'none',
            boxSizing: 'border-box'
          }}
        >
          <option value="Sativa">Sativa</option>
          <option value="Indica">Indica</option>
          <option value="Hybrid">Hybrid</option>
          <option value="CBD">CBD</option>
        </select>
      </div>
    </>
  );

    switch (currentType) {
      case 'flower':
        return (
          <div>
            {commonFields}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#e5e5e5',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Terpenes (comma-separated)
              </label>
              <input
                type="text"
                placeholder="e.g., myrcene, limonene, caryophyllene"
                value={currentAttributes.terpenes ? currentAttributes.terpenes.join(', ') : ''}
                onChange={(e) => handleTerpenesChange(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#2a2a2a',
                  border: '2px solid #333',
                  borderRadius: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#e5e5e5',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Size
              </label>
              <select
                value={currentAttributes.size || '3.5g'}
                onChange={(e) => handleChange('size', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#2a2a2a',
                  border: '2px solid #333',
                  borderRadius: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              >
                <option value="3.5g">3.5g (1/8 oz)</option>
                <option value="7g">7g (1/4 oz)</option>
                <option value="14g">14g (1/2 oz)</option>
                <option value="28g">28g (1 oz)</option>
              </select>
            </div>
          </div>
        );

      case 'cart':
        return (
          <div>
            {commonFields}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#e5e5e5',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Flavor Profile
              </label>
              <input
                type="text"
                placeholder="e.g., Berry, Sweet"
                value={currentAttributes.flavor || ''}
                onChange={(e) => handleChange('flavor', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#2a2a2a',
                  border: '2px solid #333',
                  borderRadius: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#e5e5e5',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Category
              </label>
              <select
                value={currentAttributes.category || 'Distillate'}
                onChange={(e) => handleChange('category', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#2a2a2a',
                  border: '2px solid #333',
                  borderRadius: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              >
                <option value="Distillate">Distillate</option>
                <option value="Live Resin">Live Resin</option>
                <option value="Rosin">Rosin</option>
                <option value="CO2">CO2</option>
              </select>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#e5e5e5',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Size
              </label>
              <select
                value={currentAttributes.size || '1g'}
                onChange={(e) => handleChange('size', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#2a2a2a',
                  border: '2px solid #333',
                  borderRadius: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              >
                <option value="0.5g">0.5g</option>
                <option value="1g">1g</option>
              </select>
            </div>
          </div>
        );

      case 'edible':
        return (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#e5e5e5',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                THC Content (per piece)
              </label>
              <input
                type="text"
                placeholder="e.g., 15mg per piece"
                value={currentAttributes.THC || ''}
                onChange={(e) => handleChange('THC', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#2a2a2a',
                  border: '2px solid #333',
                  borderRadius: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#e5e5e5',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                CBD Content (per piece)
              </label>
              <input
                type="text"
                placeholder="e.g., 0mg or 2mg per piece"
                value={currentAttributes.CBD || ''}
                onChange={(e) => handleChange('CBD', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#2a2a2a',
                  border: '2px solid #333',
                  borderRadius: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#e5e5e5',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Strain Type
              </label>
              <select
                value={currentAttributes.strain_type || 'Hybrid'}
                onChange={(e) => handleChange('strain_type', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#2a2a2a',
                  border: '2px solid #333',
                  borderRadius: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              >
                <option value="Sativa">Sativa</option>
                <option value="Indica">Indica</option>
                <option value="Hybrid">Hybrid</option>
                <option value="CBD">CBD</option>
              </select>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#e5e5e5',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Flavor
              </label>
              <input
                type="text"
                placeholder="e.g., Mixed Fruit"
                value={currentAttributes.flavor || ''}
                onChange={(e) => handleChange('flavor', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#2a2a2a',
                  border: '2px solid #333',
                  borderRadius: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#e5e5e5',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Category
              </label>
              <select
                value={currentAttributes.category || 'Gummies'}
                onChange={(e) => handleChange('category', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#2a2a2a',
                  border: '2px solid #333',
                  borderRadius: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              >
                <option value="Gummies">Gummies</option>
                <option value="Chocolates">Chocolates</option>
                <option value="Baked Goods">Baked Goods</option>
                <option value="Beverages">Beverages</option>
                <option value="Hard Candy">Hard Candy</option>
              </select>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#e5e5e5',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Dosage Per Unit
              </label>
              <input
                type="text"
                placeholder="e.g., 10mg per piece"
                value={currentAttributes.dosage_per_unit || ''}
                onChange={(e) => handleChange('dosage_per_unit', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#2a2a2a',
                  border: '2px solid #333',
                  borderRadius: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#e5e5e5',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Total Size
              </label>
              <input
                type="text"
                placeholder="e.g., 100mg"
                value={currentAttributes.size || ''}
                onChange={(e) => handleChange('size', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#2a2a2a',
                  border: '2px solid #333',
                  borderRadius: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>
        );

      default:
        return commonFields;
    }
  };

  // Filter uploaded products
  const filteredUploadedProducts = uploadedProducts.filter(product => {
    const matchesSearch = !searchTerm || 
      product.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.brand.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  return (
  showFeaturedConfig ? (
    <div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <button
          onClick={() => setShowFeaturedConfig(false)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            background: '#333',
            color: '#ccc',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          ← Back to Products
        </button>
        <h2 style={{
          margin: 0,
          fontSize: '24px',
          fontWeight: '700',
          color: '#e5e5e5'
        }}>
          Featured Products Configuration
        </h2>
      </div>
      <FeaturedProductsConfig />
    </div>
  ) : (
    // YOUR EXISTING JSX STARTS HERE
    <div>
      {/* Header Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '32px'
        }}>
        <div style={{
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            borderRadius: '16px',
            padding: '20px',
            color: 'white'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <Clock size={20} />
            <span style={{ fontSize: '14px', fontWeight: '500' }}>Pending Approval</span>
            </div>
            <div style={{ fontSize: '28px', fontWeight: '700' }}>{uploadedProducts.length}</div>
        </div>

        <div style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            borderRadius: '16px',
            padding: '20px',
            color: 'white'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <Package size={20} />
            <span style={{ fontSize: '14px', fontWeight: '500' }}>Live Products</span>
            </div>
            <div style={{ fontSize: '28px', fontWeight: '700' }}>{existingProducts.length}</div>
        </div>

        <div style={{
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            borderRadius: '16px',
            padding: '20px',
            color: 'white'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <Star size={20} />
            <span style={{ fontSize: '14px', fontWeight: '500' }}>Total Reviews</span>
            </div>
            <div style={{ fontSize: '28px', fontWeight: '700' }}>
            {existingProducts.reduce((sum, p) => sum + (p.reviewCount || 0), 0)}
            </div>
        </div>

        {/* Add Product Button */}
        <div style={{
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            borderRadius: '16px',
            padding: '20px',
            color: 'white',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '120px'
        }}
        onClick={() => setShowAdminUploadModal(true)}
        onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(239, 68, 68, 0.3)';
        }}
        onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
        }}
        >
            <div style={{ textAlign: 'center' }}>
            <Plus size={32} style={{ marginBottom: '8px' }} />
            <div style={{ fontSize: '16px', fontWeight: '600' }}>Add Product</div>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>Direct Admin Upload</div>
            </div>
        </div>
        </div>
          <div style={{
            display: 'flex',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <button
              onClick={() => setShowFeaturedConfig(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(251, 191, 36, 0.3)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <Award size={16} />
              Configure Featured Products
            </button>
          </div>
      {/* Search and Filters */}
      <div style={{
        background: colors.bgTertiary,
        borderRadius: '16px',
        padding: '20px',
        border: '1px solid #333',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
            <Search size={20} style={{
              position: 'absolute',
              left: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#888'
            }} />
            <input
              type="text"
              placeholder="Search products, brands..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              style={{
                width: '100%',
                paddingLeft: '48px',
                paddingRight: '16px',
                paddingTop: '12px',
                paddingBottom: '12px',
                border: '2px solid #333',
                borderRadius: '12px',
                fontSize: '14px',
                background: '#2a2a2a',
                color: '#e5e5e5',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={{
              padding: '12px 16px',
              border: '2px solid #333',
              borderRadius: '12px',
              fontSize: '14px',
              background: '#2a2a2a',
              color: '#e5e5e5',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Types</option>
            <option value="flower">Flower</option>
            <option value="cart">Cartridges</option>
            <option value="edible">Edibles</option>
            <option value="pre-roll">Pre-Rolls</option>
            <option value="concentrate">Concentrates</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      {/* Section Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        background: '#0f0f0f',
        padding: '6px',
        borderRadius: '12px',
        border: '1px solid #333',
        marginBottom: '24px'
      }}>
        <div style={{
          flex: 1,
          textAlign: 'center',
          padding: '12px',
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: 'white',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          <Clock size={16} style={{ display: 'inline-block', marginRight: '8px' }} />
          Pending Approval ({uploadedProducts.length})
        </div>
      </div>

      {/* Uploaded Products (Pending Approval) */}
      <div style={{ marginBottom: '48px' }}>
        <h3 style={{ 
          margin: '0 0 20px 0', 
          color: '#e5e5e5', 
          fontSize: '20px', 
          fontWeight: '600' 
        }}>
          Products Awaiting Approval
        </h3>
        
        <div style={{ display: 'grid', gap: '16px' }}>
          {filteredUploadedProducts.map(product => (
            <div
              key={product.id}
              style={{
                background: colors.bgTertiary,
                borderRadius: '16px',
                padding: '20px',
                border: '1px solid #333',
                transition: 'all 0.2s ease'
              }}
            >
              <div style={{ display: 'flex', gap: '20px', alignItems: 'start' }}>
                {/* Product Image */}
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '12px',
                  background: '#2a2a2a',
                  border: '1px solid #333',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}>
                  {product.label_image_url ? (
                    <img
                      src={product.label_image_url}
                      alt={product.product_name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <Image size={24} style={{ color: '#666' }} />
                  )}
                </div>

                {/* Product Info */}
                <div style={{ flex: 1 }}>
                  <h4 style={{
                    margin: '0 0 8px 0',
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#e5e5e5'
                  }}>
                    {product.product_name}
                  </h4>
                  
                  <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Tag size={14} style={{ color: '#888' }} />
                      <span style={{ color: '#888', fontSize: '14px' }}>{product.brand}</span>
                    </div>
                    
                    {product.rating && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Star size={14} style={{ color: '#f59e0b' }} />
                        <span style={{ color: '#888', fontSize: '14px' }}>User rated: {product.rating}/5</span>
                      </div>
                    )}
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Calendar size={14} style={{ color: '#888' }} />
                      <span style={{ color: '#888', fontSize: '14px' }}>
                        {new Date(product.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {product.review && (
                    <p style={{
                      margin: '0 0 16px 0',
                      color: '#ccc',
                      fontSize: '14px',
                      lineHeight: '1.5'
                    }}>
                      "{product.review}"
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center'   }}>
                  <button
                    onClick={() => handleApproveProduct(product)}
                    disabled={actionLoading}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 16px',
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: actionLoading ? 'not-allowed' : 'pointer',
                      opacity: actionLoading ? 0.6 : 1,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <CheckCircle size={16} />
                    Approve
                  </button>

                  <button
                    onClick={() => handleDenyProduct(product.id)}
                    disabled={actionLoading}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 16px',
                      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: actionLoading ? 'not-allowed' : 'pointer',
                      opacity: actionLoading ? 0.6 : 1,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <XCircle size={16} />
                    Deny
                  </button>
                </div>
              </div>
            </div>
          ))}

          {filteredUploadedProducts.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '64px 24px',
              background: colors.bgTertiary,
              borderRadius: '20px',
              border: '1px solid #333'
            }}>
              <Package size={48} style={{ color: '#666', marginBottom: '16px' }} />
              <h3 style={{ margin: '0 0 8px 0', color: '#e5e5e5', fontSize: '20px', fontWeight: '600' }}>
                No pending products
              </h3>
              <p style={{ margin: 0, color: '#888', fontSize: '16px' }}>
                All submitted products have been reviewed
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Existing Products */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ 
            margin: 0, 
            color: '#e5e5e5', 
            fontSize: '20px', 
            fontWeight: '600' 
          }}>
            Live Products ({totalProducts.toLocaleString()})
          </h3>
          
          {/* Sort Controls */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #333',
                borderRadius: '8px',
                fontSize: '14px',
                background: '#2a2a2a',
                color: '#e5e5e5'
              }}
            >
              <option value="name">Sort by Name</option>
              <option value="brand">Sort by Brand</option>
              <option value="type">Sort by Type</option>
              <option value="created_at">Sort by Date</option>
              <option value="average_rating">Sort by Rating</option>
            </select>
            
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              style={{
                padding: '8px 12px',
                background: '#333',
                color: '#ccc',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>

        {/* Quick Search for Live Products */}
        <div style={{
          background: colors.bgTertiary,
          borderRadius: '12px',
          padding: '16px',
          border: '1px solid #333',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
              <Search size={18} style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#888'
              }} />
              <input
                type="text"
                placeholder="Quick search live products..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                style={{
                  width: '100%',
                  paddingLeft: '40px',
                  paddingRight: '12px',
                  paddingTop: '10px',
                  paddingBottom: '10px',
                  border: '2px solid #333',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: '#2a2a2a',
                  color: '#e5e5e5',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={{
                padding: '10px 12px',
                border: '2px solid #333',
                borderRadius: '8px',
                fontSize: '14px',
                background: '#2a2a2a',
                color: '#e5e5e5',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Types</option>
              <option value="flower">Flower</option>
              <option value="cart">Cartridges</option>
              <option value="edible">Edibles</option>
              <option value="pre-roll">Pre-Rolls</option>
              <option value="concentrate">Concentrates</option>
              <option value="other">Other</option>
            </select>

            {(searchTerm || filterType !== 'all') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterType('all');
                }}
                style={{
                  padding: '10px 12px',
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <X size={14} />
                Clear
              </button>
            )}
          </div>
        </div>
        
        {/* Products Grid */}
        <div style={{ display: 'grid', gap: '16px', marginBottom: '24px' }}>
          {existingProducts.map(product => (
            <div
              key={product.id}
              style={{
                background: colors.bgTertiary,
                borderRadius: '16px',
                padding: '20px',
                border: '1px solid #333',
                transition: 'all 0.2s ease'
              }}
            >
              <div style={{ display: 'flex', gap: '20px', alignItems: 'start' }}>
                {/* Product Type Icon */}
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '12px',
                  background: '#2a2a2a',
                  border: '1px solid #333',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}>
                  {product.coverImage ? (
                    <img
                      src={product.coverImage.imageUrl}
                      alt={product.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  
                  <div style={{
                    display: product.coverImage ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: product.type === 'flower' ? '#10b981' : 
                                product.type === 'cart' ? '#8b5cf6' :
                                product.type === 'edible' ? '#f59e0b' :
                                product.type === 'pre-roll' ? '#06b6d4' :
                                product.type === 'concentrate' ? '#ef4444' : '#6b7280',
                    color: 'white',
                    width: '100%',
                    height: '100%'
                  }}>
                    <Package size={32} />
                  </div>
                </div>

                {/* Product Info */}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <h4 style={{
                      margin: 0,
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#e5e5e5'
                    }}>
                      {product.name}
                    </h4>
                    <span style={{
                      padding: '4px 8px',
                      background: product.type === 'flower' ? '#10b98120' : 
                                  product.type === 'cart' ? '#8b5cf620' :
                                  product.type === 'edible' ? '#f59e0b20' :
                                  product.type === 'pre-roll' ? '#06b6d420' :
                                  product.type === 'concentrate' ? '#ef444420' : '#6b728020',
                      color: product.type === 'flower' ? '#10b981' : 
                            product.type === 'cart' ? '#8b5cf6' :
                            product.type === 'edible' ? '#f59e0b' :
                            product.type === 'pre-roll' ? '#06b6d4' :
                            product.type === 'concentrate' ? '#ef4444' : '#6b7280',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '500',
                      textTransform: 'capitalize'
                    }}>
                      {product.type}
                    </span>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Tag size={14} style={{ color: '#888' }} />
                      <span style={{ color: '#888', fontSize: '14px' }}>{product.brand}</span>
                    </div>
                    
                    {product.averageRating > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Star size={14} style={{ color: '#f59e0b' }} />
                        <span style={{ color: '#888', fontSize: '14px' }}>
                          {product.averageRating} ({product.reviewCount} reviews)
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Attributes Preview */}
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    {product.attributes?.THC && (
                      <span style={{
                        padding: '2px 8px',
                        background: '#333',
                        color: '#ccc',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        THC: {product.attributes.THC}
                      </span>
                    )}
                    {product.attributes?.CBD && (
                      <span style={{
                        padding: '2px 8px',
                        background: '#333',
                        color: '#ccc',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        CBD: {product.attributes.CBD}
                      </span>
                    )}
                    {product.attributes?.strain_type && (
                      <span style={{
                        padding: '2px 8px',
                        background: '#333',
                        color: '#ccc',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        {product.attributes.strain_type}
                      </span>
                    )}
                    {product.attributes?.size && (
                      <span style={{
                        padding: '2px 8px',
                        background: '#333',
                        color: '#ccc',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        {product.attributes.size}
                      </span>
                    )}
                  </div>
                </div>

                {/* Product Actions */}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 12px',
                      background: '#333',
                      color: '#ccc',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.background = '#444';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = '#333';
                    }}
                  >
                    <Eye size={16} />
                    View
                  </button>
                  
                  <button
                    onClick={() => handleEditProduct(product)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 12px',
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Edit3 size={16} />
                    Edit
                  </button>

                  <button
                    onClick={() => handleDeleteProduct(product.id)}
                    disabled={actionLoading}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 12px',
                      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      cursor: actionLoading ? 'not-allowed' : 'pointer',
                      opacity: actionLoading ? 0.6 : 1,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}

          {existingProducts.length === 0 && !loading && (
            <div style={{
              textAlign: 'center',
              padding: '64px 24px',
              background: colors.bgTertiary,
              borderRadius: '20px',
              border: '1px solid #333'
            }}>
              <Package size={48} style={{ color: '#666', marginBottom: '16px' }} />
              <h3 style={{ margin: '0 0 8px 0', color: '#e5e5e5', fontSize: '20px', fontWeight: '600' }}>
                No products found
              </h3>
              <p style={{ margin: 0, color: '#888', fontSize: '16px' }}>
                {searchTerm || filterType !== 'all' 
                  ? 'Try adjusting your search or filter criteria'
                  : 'No products have been approved yet'}
              </p>
            </div>
          )}

          {loading && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#888'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                border: '3px solid #333',
                borderTop: '3px solid #8b5cf6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 16px'
              }} />
              Loading products...
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '24px',
            padding: '20px',
            background: colors.bgTertiary,
            borderRadius: '12px',
            border: '1px solid #333'
          }}>
            {/* Results Info */}
            <div style={{ color: '#888', fontSize: '14px' }}>
              Showing {((currentPage - 1) * perPage) + 1}-{Math.min(currentPage * perPage, totalProducts)} of {totalProducts.toLocaleString()} products
            </div>
            
            {/* Page Size Selector */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#888', fontSize: '14px' }}>Show:</span>
              <select
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{
                  padding: '4px 8px',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: '#2a2a2a',
                  color: '#e5e5e5'
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            
            {/* Pagination Buttons */}
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              {/* First Page */}
              <button
                onClick={() => setCurrentPage(1)}
                disabled={!hasPrev}
                style={{
                  padding: '8px',
                  background: hasPrev ? '#333' : '#222',
                  color: hasPrev ? '#ccc' : '#555',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: hasPrev ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ChevronsLeft size={16} />
              </button>
              
              {/* Previous Page */}
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={!hasPrev}
                style={{
                  padding: '8px',
                  background: hasPrev ? '#333' : '#222',
                  color: hasPrev ? '#ccc' : '#555',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: hasPrev ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ChevronLeft size={16} />
              </button>
              
              {/* Page Numbers */}
              <div style={{ display: 'flex', gap: '2px', alignItems: 'center', margin: '0 8px' }}>
                {/* Show page numbers around current page */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      style={{
                        padding: '8px 12px',
                        background: pageNum === currentPage ? '#8b5cf6' : '#333',
                        color: pageNum === currentPage ? 'white' : '#ccc',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        cursor: 'pointer',
                        minWidth: '36px'
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                {totalPages > 5 && currentPage < totalPages - 2 && (
                  <>
                    <span style={{ color: '#666', margin: '0 4px' }}>...</span>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      style={{
                        padding: '8px 12px',
                        background: '#333',
                        color: '#ccc',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        cursor: 'pointer',
                        minWidth: '36px'
                      }}
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>
              
              {/* Next Page */}
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={!hasNext}
                style={{
                  padding: '8px',
                  background: hasNext ? '#333' : '#222',
                  color: hasNext ? '#ccc' : '#555',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: hasNext ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ChevronRight size={16} />
              </button>
              
              {/* Last Page */}
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={!hasNext}
                style={{
                  padding: '8px',
                  background: hasNext ? '#333' : '#222',
                  color: hasNext ? '#ccc' : '#555',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: hasNext ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Product Modal */}
      {showEditModal && editingProduct && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: colors.bgTertiary,
            borderRadius: '20px',
            padding: '32px',
            border: '1px solid #333',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
              <h3 style={{
                margin: 0,
                fontSize: '24px',
                fontWeight: '700',
                color: '#e5e5e5'
              }}>
                Edit Product: {editingProduct.name}
              </h3>
              <button
                onClick={() => setShowEditModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#888',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Basic Product Info */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#e5e5e5',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Product Name
              </label>
              <input
                type="text"
                placeholder="e.g., Blue Dream"
                value={editProductData.name}
                onChange={(e) => setEditProductData(prev => ({ ...prev, name: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#2a2a2a',
                  border: '2px solid #333',
                  borderRadius: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#e5e5e5',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Brand
              </label>
              <input
                type="text"
                placeholder="e.g., Green Garden"
                value={editProductData.brand}
                onChange={(e) => setEditProductData(prev => ({ ...prev, brand: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#2a2a2a',
                  border: '2px solid #333',
                  borderRadius: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Product Type Selection */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  fontWeight: '500'
              }}>
                  Product Type
              </label>
              <select
                  value={selectedProductType}
                  onChange={(e) => {
                      const newType = e.target.value;
                      setSelectedProductType(newType);
                      
                      // Preserve existing attributes when changing type
                      let updatedAttributes = {};
                      
                      if (newType === 'cart') {
                          updatedAttributes = {
                              THC: productAttributes.THC || '',
                              CBD: productAttributes.CBD || '',
                              flavor: productAttributes.flavor || '',
                              terpenes: productAttributes.terpenes || [],
                              strain_type: productAttributes.strain_type || 'Hybrid',
                              category: productAttributes.category || 'Distillate',
                              size: productAttributes.size || '1g'
                          };
                      } else if (newType === 'edible') {
                        updatedAttributes = {
                            THC: productAttributes.THC || '',
                            CBD: productAttributes.CBD || '',
                            flavor: productAttributes.flavor || '',
                            strain_type: productAttributes.strain_type || 'Hybrid',
                            category: productAttributes.category || 'Gummies',
                            dosage_per_unit: productAttributes.dosage_per_unit || '',
                            size: productAttributes.size || '',
                            terpenes: productAttributes.terpenes || [] // ADD TERPENES FOR EDIBLE
                        };
                    } else if (newType === 'pre-roll') {
                        updatedAttributes = {
                            THC: productAttributes.THC || '',
                            CBD: productAttributes.CBD || '',
                            strain_type: productAttributes.strain_type || 'Hybrid',
                            terpenes: productAttributes.terpenes || [], // ALREADY HAS TERPENES
                            size: productAttributes.size || '1g',
                            quantity: productAttributes.quantity || '1-Pack'
                        };
                    } else if (newType === 'concentrate') {
                        updatedAttributes = {
                            THC: productAttributes.THC || '',
                            CBD: productAttributes.CBD || '',
                            strain_type: productAttributes.strain_type || 'Hybrid',
                            category: productAttributes.category || 'Shatter',
                            size: productAttributes.size || '1g',
                            terpenes: productAttributes.terpenes || [] // ADD TERPENES FOR CONCENTRATE
                        };
                    } else if (newType === 'other') {
                        updatedAttributes = {
                            THC: productAttributes.THC || '',
                            CBD: productAttributes.CBD || '',
                            strain_type: productAttributes.strain_type || 'CBD',
                            category: productAttributes.category || 'Tincture',
                            size: productAttributes.size || '',
                            flavor: productAttributes.flavor || '',
                            terpenes: productAttributes.terpenes || [] // ADD TERPENES FOR OTHER
                        };
                    } else {
                        // flower - ALREADY HAS TERPENES
                        updatedAttributes = {
                            THC: productAttributes.THC || '',
                            CBD: productAttributes.CBD || '',
                            terpenes: productAttributes.terpenes || [],
                            strain_type: productAttributes.strain_type || 'Hybrid',
                            size: productAttributes.size || '3.5g'
                        };
                    }
                      
                      setProductAttributes(updatedAttributes);
                  }}
                  style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                  }}
              >
                  <option value="flower">Flower</option>
                  <option value="cart">Cartridge/Vape</option>
                  <option value="edible">Edible</option>
                  <option value="pre-roll">Pre-Roll</option>
                  <option value="concentrate">Concentrate</option>
                  <option value="other">Other</option>
              </select>
          </div>

            {/* Dynamic Attribute Form */}
            {renderAttributeForm(true)}
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{
                margin: '0 0 16px 0',
                fontSize: '18px',
                fontWeight: '600',
                color: '#e5e5e5'
              }}>
                Product Images
              </h4>
              
              {/* Upload Cover Image */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Upload Cover Image
                </label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'end' }}>
                  <div style={{ flex: 1 }}>
                    <input
                      id="cover-file-input"
                      type="file"
                      accept="image/*"
                      onChange={handleCoverFileSelect}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: '#2a2a2a',
                        border: '2px solid #333',
                        borderRadius: '8px',
                        color: '#e5e5e5',
                        fontSize: '14px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                    {selectedCoverFile && (
                      <div style={{
                        marginTop: '4px',
                        color: '#888',
                        fontSize: '12px'
                      }}>
                        {selectedCoverFile.name} selected
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={handleUploadCoverImage}
                    disabled={!selectedCoverFile || uploadingCover}
                    style={{
                      padding: '12px 16px',
                      background: (selectedCoverFile && !uploadingCover) 
                        ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' 
                        : '#555',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: (selectedCoverFile && !uploadingCover) ? 'pointer' : 'not-allowed',
                      transition: 'all 0.2s ease',
                      minWidth: '140px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                  >
                    {uploadingCover ? (
                      <>
                        <div style={{
                          width: '12px',
                          height: '12px',
                          border: '2px solid transparent',
                          borderTop: '2px solid white',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                        Uploading Cover...
                      </>
                    ) : (
                      'Upload Cover to S3'
                    )}
                  </button>
                </div>
              </div>
              
              {/* Upload Multiple Images */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Upload Additional Images
                </label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'end' }}>
                  <div style={{ flex: 1 }}>
                    <input
                      id="image-file-input"
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFileSelect}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: '#2a2a2a',
                        border: '2px solid #333',
                        borderRadius: '8px',
                        color: '#e5e5e5',
                        fontSize: '14px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                    {selectedFiles.length > 0 && (
                      <div style={{
                        marginTop: '4px',
                        color: '#888',
                        fontSize: '12px'
                      }}>
                        {selectedFiles.length} file(s) selected
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={handleUploadImages}
                    disabled={selectedFiles.length === 0 || uploadingImages}
                    style={{
                      padding: '12px 16px',
                      background: (selectedFiles.length > 0 && !uploadingImages) 
                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                        : '#555',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: (selectedFiles.length > 0 && !uploadingImages) ? 'pointer' : 'not-allowed',
                      transition: 'all 0.2s ease',
                      minWidth: '120px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                  >
                    {uploadingImages ? (
                      <>
                        <div style={{
                          width: '12px',
                          height: '12px',
                          border: '2px solid transparent',
                          borderTop: '2px solid white',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                        Uploading...
                      </>
                    ) : (
                      'Upload to S3'
                    )}
                  </button>
                </div>
                <div style={{
                  marginTop: '4px',
                  color: '#888',
                  fontSize: '11px'
                }}>
                  Images are uploaded to S3 immediately. Changes saved to database when you click "Save Changes".
                </div>
              </div>

              {/* Current Images (S3 URLs stored in frontend state) */}
              {editProductImages.length > 0 && (
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    Current Images (ready to save)
                  </label>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {editProductImages.map((image) => (
                      <div
                        key={image.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px',
                          background: '#2a2a2a',
                          border: image.imageUrl === coverImageUrl ? '2px solid #8b5cf6' : '1px solid #333',
                          borderRadius: '8px'
                        }}
                      >
                        {/* Image Preview */}
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '6px',
                          background: '#333',
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <img
                            src={image.imageUrl}
                            alt="Product"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <Image size={16} style={{ color: '#666', display: 'none' }} />
                        </div>
                        
                        {/* Image Info */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            color: '#e5e5e5',
                            fontSize: '12px',
                            fontWeight: '500',
                            marginBottom: '2px'
                          }}>
                            {image.imageUrl === coverImageUrl && (
                              <span style={{
                                color: '#8b5cf6',
                                marginRight: '6px'
                              }}>
                                [COVER]
                              </span>
                            )}
                            S3 Image (not saved to DB yet)
                          </div>
                          <div style={{
                            color: '#888',
                            fontSize: '11px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {image.imageUrl.split('/').pop()}
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div style={{ display: 'flex', gap: '4px' }}>
                          {image.imageUrl !== coverImageUrl && (
                            <button
                              onClick={() => handleSetCoverImage(image.imageUrl)}
                              style={{
                                padding: '4px 8px',
                                background: '#8b5cf6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '10px',
                                cursor: 'pointer'
                              }}
                            >
                              Set Cover
                            </button>
                          )}
                          
                          <button
                            onClick={() => handleRemoveImage(image)}
                            style={{
                              padding: '4px 8px',
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '10px',
                              cursor: 'pointer'
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
              <button
                onClick={() => setShowEditModal(false)}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: '#333',
                  color: '#ccc',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Cancel
              </button>
              
              <button
                onClick={handleSaveEditedProduct}
                disabled={actionLoading}
                style={{
                  flex: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '16px',
                  background: actionLoading ? '#555' : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {actionLoading ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid transparent',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Attribute Modal (for approving new products) */}
      {showAttributeModal && selectedProduct && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: colors.bgTertiary,
            borderRadius: '20px',
            padding: '32px',
            border: '1px solid #333',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
              <h3 style={{
                margin: 0,
                fontSize: '24px',
                fontWeight: '700',
                color: '#e5e5e5'
              }}>
                Approve Product: {selectedProduct.product_name}
              </h3>
              <button
                onClick={() => setShowAttributeModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#888',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Product Type Selection */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  fontWeight: '500'
              }}>
                  Product Type
              </label>
              <select
                  value={selectedProductType}
                  onChange={(e) => {
                      const newType = e.target.value;
                      setSelectedProductType(newType);
                      
                      // Preserve existing attributes when changing type
                      let updatedAttributes = {};
                      
                      if (newType === 'cart') {
                          updatedAttributes = {
                              THC: productAttributes.THC || '',
                              CBD: productAttributes.CBD || '',
                              flavor: productAttributes.flavor || '',
                              strain_type: productAttributes.strain_type || 'Hybrid',
                              category: productAttributes.category || 'Distillate',
                              size: productAttributes.size || '1g',
                              terpenes: productAttributes.terpenes || [] // ADD TERPENES FOR CART
                          };
                      } else if (newType === 'edible') {
                          updatedAttributes = {
                              THC: productAttributes.THC || '',
                              CBD: productAttributes.CBD || '',
                              flavor: productAttributes.flavor || '',
                              strain_type: productAttributes.strain_type || 'Hybrid',
                              category: productAttributes.category || 'Gummies',
                              dosage_per_unit: productAttributes.dosage_per_unit || '',
                              size: productAttributes.size || ''
                          };
                      } else if (newType === 'pre-roll') {
                          updatedAttributes = {
                              THC: productAttributes.THC || '',
                              CBD: productAttributes.CBD || '',
                              strain_type: productAttributes.strain_type || 'Hybrid',
                              terpenes: productAttributes.terpenes || [], // PRESERVE TERPENES!
                              size: productAttributes.size || '1g',
                              quantity: productAttributes.quantity || '1-Pack'
                          };
                      } else if (newType === 'concentrate') {
                          updatedAttributes = {
                              THC: productAttributes.THC || '',
                              CBD: productAttributes.CBD || '',
                              strain_type: productAttributes.strain_type || 'Hybrid',
                              category: productAttributes.category || 'Shatter',
                              size: productAttributes.size || '1g'
                          };
                      } else if (newType === 'other') {
                          updatedAttributes = {
                              THC: productAttributes.THC || '',
                              CBD: productAttributes.CBD || '',
                              strain_type: productAttributes.strain_type || 'CBD',
                              category: productAttributes.category || 'Tincture',
                              size: productAttributes.size || '',
                              flavor: productAttributes.flavor || ''
                          };
                      } else {
                          // flower - PRESERVE TERPENES!
                          updatedAttributes = {
                              THC: productAttributes.THC || '',
                              CBD: productAttributes.CBD || '',
                              terpenes: productAttributes.terpenes || [], // PRESERVE TERPENES!
                              strain_type: productAttributes.strain_type || 'Hybrid',
                              size: productAttributes.size || '3.5g'
                          };
                      }
                      
                      setProductAttributes(updatedAttributes);
                  }}
                  style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                  }}
              >
                  <option value="flower">Flower</option>
                  <option value="cart">Cartridge/Vape</option>
                  <option value="edible">Edible</option>
                  <option value="pre-roll">Pre-Roll</option>
                  <option value="concentrate">Concentrate</option>
                  <option value="other">Other</option>
              </select>
          </div>

            {/* Dynamic Attribute Form */}
            {renderAttributeForm(false)}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
              <button
                onClick={() => setShowAttributeModal(false)}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: '#333',
                  color: '#ccc',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Cancel
              </button>
              
              <button
                onClick={handleSaveProduct}
                disabled={actionLoading}
                style={{
                  flex: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '16px',
                  background: actionLoading ? '#555' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {actionLoading ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid transparent',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Processing...
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    Approve & Save Product
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Upload Modal */}
      {showAdminUploadModal && (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
        }}>
            <div style={{
            background: colors.bgTertiary,
            borderRadius: '20px',
            padding: '32px',
            border: '1px solid #333',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
            }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                <h3 style={{
                margin: 0,
                fontSize: '24px',
                fontWeight: '700',
                color: '#e5e5e5'
                }}>
                Add New Product
                </h3>
                <button
                onClick={() => setShowAdminUploadModal(false)}
                style={{
                    background: 'none',
                    border: 'none',
                    color: '#888',
                    cursor: 'pointer',
                    padding: '4px'
                }}
                >
                <X size={24} />
                </button>
            </div>

            {/* Basic Product Info */}
            <div style={{ marginBottom: '24px' }}>
                <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#e5e5e5',
                fontSize: '14px',
                fontWeight: '500'
                }}>
                Product Name
                </label>
                <input
                type="text"
                placeholder="e.g., Blue Dream"
                value={adminUploadData.name}
                onChange={(e) => setAdminUploadData(prev => ({ ...prev, name: e.target.value }))}
                style={{
                    width: '100%',
                    padding: '12px',
                    background: '#2a2a2a',
                    border: '2px solid #333',
                    borderRadius: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                }}
                />
            </div>

            <div style={{ marginBottom: '24px' }}>
                <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#e5e5e5',
                fontSize: '14px',
                fontWeight: '500'
                }}>
                Brand
                </label>
                <input
                type="text"
                placeholder="e.g., Green Garden"
                value={adminUploadData.brand}
                onChange={(e) => setAdminUploadData(prev => ({ ...prev, brand: e.target.value }))}
                style={{
                    width: '100%',
                    padding: '12px',
                    background: '#2a2a2a',
                    border: '2px solid #333',
                    borderRadius: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                }}
                />
            </div>

            {/* Product Type Selection */}
            <div style={{ marginBottom: '24px' }}>
                <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#e5e5e5',
                fontSize: '14px',
                fontWeight: '500'
                }}>
                Product Type
                </label>
                <select
                value={adminUploadData.type}
                onChange={(e) => handleAdminTypeChange(e.target.value)}
                style={{
                    width: '100%',
                    padding: '12px',
                    background: '#2a2a2a',
                    border: '2px solid #333',
                    borderRadius: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                }}
                >
                <option value="flower">Flower</option>
                <option value="cart">Cartridge/Vape</option>
                <option value="edible">Edible</option>
                <option value="pre-roll">Pre-Roll</option>
                <option value="concentrate">Concentrate</option>
                <option value="other">Other</option>
                </select>
            </div>

            {/* Render admin form attributes - simplified version */}
            <div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#e5e5e5', fontSize: '14px', fontWeight: '500' }}>THC Content</label>
                <input type="text" placeholder="e.g., 21%" value={adminUploadData.attributes.THC || ''} onChange={(e) => handleAdminAttributeChange('THC', e.target.value)} style={{ width: '100%', padding: '12px', background: '#2a2a2a', border: '2px solid #333', borderRadius: '8px', color: '#e5e5e5', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#e5e5e5', fontSize: '14px', fontWeight: '500' }}>CBD Content</label>
                <input type="text" placeholder="e.g., 0.5%" value={adminUploadData.attributes.CBD || ''} onChange={(e) => handleAdminAttributeChange('CBD', e.target.value)} style={{ width: '100%', padding: '12px', background: '#2a2a2a', border: '2px solid #333', borderRadius: '8px', color: '#e5e5e5', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#e5e5e5', fontSize: '14px', fontWeight: '500' }}>Strain Type</label>
                <select value={adminUploadData.attributes.strain_type || 'Hybrid'} onChange={(e) => handleAdminAttributeChange('strain_type', e.target.value)} style={{ width: '100%', padding: '12px', background: '#2a2a2a', border: '2px solid #333', borderRadius: '8px', color: '#e5e5e5', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}>
                  <option value="Sativa">Sativa</option>
                  <option value="Indica">Indica</option>
                  <option value="Hybrid">Hybrid</option>
                  <option value="CBD">CBD</option>
                </select>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
                <button
                onClick={() => setShowAdminUploadModal(false)}
                style={{
                    flex: 1,
                    padding: '16px',
                    background: '#333',
                    color: '#ccc',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                }}
                >
                Cancel
                </button>
                
                <button
                onClick={handleAdminUpload}
                disabled={actionLoading || !adminUploadData.name || !adminUploadData.brand}
                style={{
                    flex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '16px',
                    background: (actionLoading || !adminUploadData.name || !adminUploadData.brand) ? '#555' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: (actionLoading || !adminUploadData.name || !adminUploadData.brand) ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease'
                }}
                >
                {actionLoading ? (
                    <>
                    <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid transparent',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }} />
                    Adding...
                    </>
                ) : (
                    <>
                    <Plus size={20} />
                    Add Product
                    </>
                )}
                </button>
            </div>
            </div>
        </div>
        )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      </div>
    )
  );
};